import { useEffect, useState } from "react";
import { Award, ChevronLeft, ChevronRight, Crown, HandHeart, Loader2, Medal, Trophy, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { inputCls, btnPrimary, Skeleton } from "@/components/portal/ui";
import { Switch } from "@/components/ui/switch";
import { AdminTitle } from "@/pages/admin/AdminLayout";
import { monthLabel, shiftMonth } from "@/pages/portal/Leaderboard";

const thisMonth = () => new Date().toISOString().slice(0, 7);
const MEDAL = ["text-amber-500", "text-slate-400", "text-orange-400"];

function RankIcon({ rank }) {
  if (rank === 1) return <Crown className={`w-5 h-5 mx-auto ${MEDAL[0]}`} />;
  if (rank <= 3) return <Medal className={`w-5 h-5 mx-auto ${MEDAL[rank - 1]}`} />;
  return <span className="text-slate-300 font-black tabular-nums">{rank}</span>;
}

function MonthPicker({ month, setMonth, testid }) {
  return (
    <div className="flex items-center gap-1">
      <button onClick={() => setMonth((m) => shiftMonth(m, -1))} data-testid={`${testid}-prev`} className="w-8 h-8 rounded-lg grid place-items-center text-slate-500 hover:bg-slate-100"><ChevronLeft className="w-4 h-4" /></button>
      <span className="text-xs font-bold text-slate-600 tabular-nums whitespace-nowrap" data-testid={`${testid}-label`}>{monthLabel(month)}</span>
      <button disabled={month >= thisMonth()} onClick={() => setMonth((m) => shiftMonth(m, 1))} data-testid={`${testid}-next`} className="w-8 h-8 rounded-lg grid place-items-center text-slate-500 hover:bg-slate-100 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
    </div>
  );
}

function AttendanceBoard() {
  const [month, setMonth] = useState(thisMonth);
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/admin/leaderboard/attendance?month=${month}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [month]);
  return (
    <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100" data-testid="admin-attendance-board">
      <div className="flex items-center justify-between gap-3">
        <p className="font-extrabold text-slate-900 flex items-center gap-2"><Users className="w-4 h-4 text-blue-600" />Jemaat Paling Rajin Ibadah</p>
        <MonthPicker month={month} setMonth={setMonth} testid="attendance-month" />
      </div>
      {!data ? <Skeleton className="h-64 mt-4" /> : data.leaderboard.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500" data-testid="attendance-board-empty">Belum ada check-in kehadiran pada bulan ini.</p>
      ) : (
        <>
          <p className="mt-1 text-xs text-slate-500">{data.members} jemaat · {data.total_checkins} check-in</p>
          <div className="mt-3 divide-y divide-slate-50 max-h-[26rem] overflow-y-auto">
            {data.leaderboard.map((m) => (
              <div key={m.user_id} className="py-2.5 flex items-center gap-3" data-testid={`attendance-row-${m.rank}`}>
                <span className="w-7 shrink-0 text-center"><RankIcon rank={m.rank} /></span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-bold text-slate-900 truncate">{m.name}</span>
                  <span className="block text-[11px] text-slate-400 truncate">NIG {m.nig || "-"}{m.favorite_service ? ` · ${m.favorite_service}` : ""}</span>
                </span>
                <span className="shrink-0 text-right">
                  <span className="block text-sm font-black text-blue-600 tabular-nums">{m.count}x</span>
                  <span className="block text-[10px] text-slate-400">{m.weeks} hari</span>
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ServantBoard() {
  const [month, setMonth] = useState(thisMonth);
  const [data, setData] = useState(null);
  const [awards, setAwards] = useState([]);
  const [issuing, setIssuing] = useState(false);

  const load = () => {
    setData(null);
    api.get(`/admin/leaderboard/servants?month=${month}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get(`/admin/ministry/awards?month=${month}`).then((r) => setAwards(r.data)).catch(() => setAwards([]));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => load(), [month]);

  const issue = async () => {
    setIssuing(true);
    try {
      const { data: r } = await api.post(`/admin/ministry/awards/issue?month=${month}`);
      toast.success(`${r.issued.length} piagam diterbitkan${r.skipped ? `, ${r.skipped} sudah ada` : ""}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setIssuing(false);
    }
  };

  return (
    <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100" data-testid="admin-servant-board">
      <div className="flex items-center justify-between gap-3">
        <p className="font-extrabold text-slate-900 flex items-center gap-2"><HandHeart className="w-4 h-4 text-rose-600" />Pelayan Terbaik Bulanan</p>
        <MonthPicker month={month} setMonth={setMonth} testid="servant-month" />
      </div>
      {!data ? <Skeleton className="h-64 mt-4" /> : data.leaderboard.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500" data-testid="servant-board-empty">Belum ada kehadiran pelayanan tercatat bulan ini.</p>
      ) : (
        <>
          <p className="mt-1 text-xs text-slate-500">{data.servants} pelayan · {data.total_served} kehadiran pelayanan</p>
          <div className="mt-3 divide-y divide-slate-50 max-h-80 overflow-y-auto">
            {data.leaderboard.map((s) => (
              <div key={s.user_id} className="py-2.5 flex items-center gap-3" data-testid={`servant-row-${s.rank}`}>
                <span className="w-7 shrink-0 text-center"><RankIcon rank={s.rank} /></span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-bold text-slate-900 truncate">{s.name}</span>
                  <span className="block text-[11px] text-slate-400 truncate">{s.roles.join(" · ") || "Pelayan"}</span>
                </span>
                <span className="shrink-0 text-right">
                  <span className="block text-sm font-black text-rose-600 tabular-nums">{s.served}x</span>
                  <span className="block text-[10px] text-slate-400">{s.rate}% hadir</span>
                </span>
              </div>
            ))}
          </div>
        </>
      )}
      <button onClick={issue} disabled={issuing} data-testid="awards-issue-button" className={`${btnPrimary} w-full mt-5`}>
        {issuing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Award className="w-4 h-4" /> Terbitkan Piagam {monthLabel(month)}</>}
      </button>
      {awards.length > 0 && (
        <div className="mt-4 rounded-2xl bg-slate-50 p-4" data-testid="awards-issued-list">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Piagam terbit ({awards.length})</p>
          {awards.map((a) => (
            <p key={a.id} className="mt-1.5 text-sm text-slate-700" data-testid={`award-item-${a.id}`}><b>{a.name}</b> · {a.title} · No. {a.number}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function SettingsCard() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/leaderboard/settings").then((r) => setCfg(r.data)); }, []);
  if (!cfg) return <Skeleton className="h-64" />;
  const setTitle = (i, v) => setCfg({ ...cfg, award_titles: cfg.award_titles.map((t, k) => (k === i ? v : t)) });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/admin/leaderboard/settings", { ...cfg, top_n: Number(cfg.top_n), min_attendance: Number(cfg.min_attendance), award_top: Number(cfg.award_top) });
      setCfg(data);
      toast.success("Pengaturan peringkat disimpan");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 space-y-4" data-testid="leaderboard-settings">
      <p className="font-extrabold text-slate-900 flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" />Pengaturan Peringkat & Apresiasi</p>
      <label className="flex items-center gap-3 text-sm font-bold text-slate-700">
        <Switch checked={cfg.show_attendance} onCheckedChange={(v) => setCfg({ ...cfg, show_attendance: v })} data-testid="cfg-show-attendance" />
        Tampilkan peringkat kehadiran di Portal Jemaat
      </label>
      <label className="flex items-center gap-3 text-sm font-bold text-slate-700">
        <Switch checked={cfg.show_servant} onCheckedChange={(v) => setCfg({ ...cfg, show_servant: v })} data-testid="cfg-show-servant" />
        Tampilkan peringkat pelayan di Portal Jemaat
      </label>
      <label className="flex items-center gap-3 text-sm font-bold text-slate-700">
        <Switch checked={cfg.auto_award} onCheckedChange={(v) => setCfg({ ...cfg, auto_award: v })} data-testid="cfg-auto-award" />
        Terbitkan piagam pelayan otomatis tiap tanggal 1
      </label>
      <div className="grid sm:grid-cols-3 gap-3">
        <label className="block"><span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Tampilkan top</span>
          <input type="number" min={3} max={50} className={`${inputCls} mt-1`} value={cfg.top_n} onChange={(e) => setCfg({ ...cfg, top_n: e.target.value })} data-testid="cfg-top-n" /></label>
        <label className="block"><span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Min. kehadiran</span>
          <input type="number" min={1} max={30} className={`${inputCls} mt-1`} value={cfg.min_attendance} onChange={(e) => setCfg({ ...cfg, min_attendance: e.target.value })} data-testid="cfg-min-attendance" /></label>
        <label className="block"><span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Penerima piagam</span>
          <input type="number" min={1} max={10} className={`${inputCls} mt-1`} value={cfg.award_top} onChange={(e) => setCfg({ ...cfg, award_top: e.target.value })} data-testid="cfg-award-top" /></label>
      </div>
      <label className="block"><span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Judul papan peringkat kehadiran</span>
        <input className={`${inputCls} mt-1`} value={cfg.attendance_title} onChange={(e) => setCfg({ ...cfg, attendance_title: e.target.value })} data-testid="cfg-attendance-title" /></label>
      <div>
        <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Judul piagam pelayan (peringkat 1–3)</span>
        <div className="mt-1 space-y-2">
          {(cfg.award_titles || []).slice(0, 3).map((t, i) => (
            <input key={i} className={inputCls} value={t} onChange={(e) => setTitle(i, e.target.value)} data-testid={`cfg-award-title-${i}`} />
          ))}
        </div>
      </div>
      <button disabled={busy} className={`${btnPrimary} w-full`} data-testid="leaderboard-settings-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Pengaturan"}</button>
    </form>
  );
}

export default function LeaderboardAdmin() {
  return (
    <div data-testid="admin-leaderboard-page">
      <AdminTitle eyebrow="Apresiasi" title="Peringkat & Apresiasi" />
      <div className="grid lg:grid-cols-2 gap-6">
        <AttendanceBoard />
        <ServantBoard />
        <div className="lg:col-span-2 grid lg:grid-cols-2 gap-6"><SettingsCard /></div>
      </div>
    </div>
  );
}
